import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Plus, FileText, ChevronRight, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Templates() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const navigate = useNavigate();

  const load = () => Promise.all([api.listTemplates(), api.listSuppliers()]).then(([t, s]) => { setItems(t); setSuppliers(s); });
  useEffect(() => { load(); }, []);

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "—";

  const doDelete = async () => {
    const tpl = confirmDelete;
    setConfirmDelete(null);
    if (!tpl) return;
    try {
      await api.deleteTemplate(tpl.id);
      toast.success("Plantilla eliminada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
        <div>
          <p className="text-[10px] text-[#5A5D6E] uppercase tracking-[0.2em] font-medium">Pedidos</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mt-2">Plantillas</h1>
          <p className="text-sm text-[#5A5D6E] mt-2">Plantillas de pedido reutilizables por proveedor.</p>
        </div>
        <Button onClick={() => navigate("/plantillas/nueva")} className="bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-sm w-full md:w-auto" data-testid="template-new-button">
          <Plus className="w-4 h-4 mr-2" /> Nueva plantilla
        </Button>
      </div>

      <div className="bg-white border border-[#E2E4E9] rounded-sm overflow-x-auto">
        {items.length === 0 ? (
          <div className="py-16 text-center">
            <FileText className="w-12 h-12 text-[#E2E4E9] mx-auto" strokeWidth={1.25} />
            <p className="text-sm text-[#5A5D6E] mt-3">Sin plantillas. Creá una para empezar.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F7F7F9] border-b border-[#E2E4E9]">
              <tr>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Nombre</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Proveedor</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Items</th>
                <th className="w-40 p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-b border-[#E2E4E9] hover:bg-[#F7F7F9]" data-testid={`template-row-${t.id}`}>
                  <td className="p-3 font-medium">{t.name}</td>
                  <td className="p-3 text-[#5A5D6E]">{supName(t.supplier_id)}</td>
                  <td className="p-3 font-mono text-xs">{t.items?.length || 0}</td>
                  <td className="p-3 flex gap-1 justify-end">
                    <button onClick={() => navigate(`/plantillas/${t.id}`)} className="p-1.5 hover:bg-[#F3F4F6] rounded-sm" data-testid={`template-edit-${t.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setConfirmDelete(t)} className="p-1.5 hover:bg-[#D32F2F]/10 text-[#D32F2F] rounded-sm" data-testid={`template-delete-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="confirm-template-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Eliminar plantilla</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  ¿Estás seguro de eliminar la plantilla <strong>{confirmDelete.name}</strong>?
                  <br />Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90 rounded-sm" data-testid="confirm-template-delete-button">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function TemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "nueva";

  const [name, setName] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importChoice, setImportChoice] = useState(null); // {items, count} when waiting for replace/append

  useEffect(() => {
    api.listSuppliers().then(setSuppliers);
    if (!isNew) {
      api.getTemplate(id).then((t) => {
        setName(t.name); setSupplierId(t.supplier_id); setNotes(t.notes || ""); setItems(t.items || []);
      });
    }
  }, [id, isNew]);

  const addEmpty = () => setItems([...items, { reference: "", description: "", quantity: 1, unit: "u", product_id: null }]);
  const updateItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await api.importItems(file);
      if (res.count === 0) {
        toast.warning("No se encontraron filas para importar");
      } else if (items.length === 0) {
        // No existing items: just append directly
        setItems([...res.items]);
        toast.success(`${res.count} ${res.count === 1 ? "item importado" : "items importados"}`);
      } else {
        // Items exist: ask user
        setImportChoice(res);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar archivo");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const applyImport = (mode) => {
    if (!importChoice) return;
    const { items: newItems, count } = importChoice;
    if (mode === "replace") {
      setItems([...newItems]);
      toast.success(`${count} item${count !== 1 ? "s" : ""} reemplazaron la lista`);
    } else {
      setItems([...items, ...newItems]);
      toast.success(`${count} item${count !== 1 ? "s" : ""} agregado${count !== 1 ? "s" : ""} al final`);
    }
    setImportChoice(null);
  };

  const onSave = async () => {
    if (!name || !supplierId) { toast.error("Completá nombre y proveedor"); return; }
    if (items.length === 0) { toast.error("Agregá al menos un item"); return; }
    const payload = {
      name, supplier_id: supplierId, notes,
      items: items.map((it) => ({
        reference: it.reference, description: it.description,
        quantity: parseFloat(it.quantity) || 0, unit: it.unit || "",
        product_id: it.product_id || null
      })),
    };
    try {
      if (isNew) await api.createTemplate(payload);
      else await api.updateTemplate(id, payload);
      toast.success("Plantilla guardada");
      navigate("/plantillas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex items-center gap-2 text-xs text-[#5A5D6E] mb-2">
        <span className="cursor-pointer hover:text-[#002FA7]" onClick={() => navigate("/plantillas")}>Plantillas</span>
        <ChevronRight className="w-3 h-3" />
        <span>{isNew ? "Nueva" : "Editar"}</span>
      </div>
      <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-6 md:mb-8">{isNew ? "Nueva plantilla" : "Editar plantilla"}</h1>

      <div className="bg-white border border-[#E2E4E9] p-4 md:p-6 rounded-sm mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Nombre *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Pedido mensual de oficina" data-testid="template-input-name" />
          </div>
          <div>
            <Label>Proveedor *</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="template-supplier-select"><SelectValue placeholder="Seleccionar proveedor" /></SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          <Label>Notas</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      <div className="bg-white border border-[#E2E4E9] rounded-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-[#E2E4E9]">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Productos del pedido</p>
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={onImportFile}
              className="hidden"
              data-testid="template-import-file"
            />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing} className="rounded-sm h-8" data-testid="template-import-button">
              <Upload className="w-3.5 h-3.5 mr-1" /> {importing ? "Importando…" : "Importar CSV/Excel"}
            </Button>
            <Button size="sm" onClick={addEmpty} className="bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-sm h-8" data-testid="template-add-row">
              <Plus className="w-3.5 h-3.5 mr-1" /> Agregar fila
            </Button>
          </div>
        </div>
        {items.length === 0 ? (
          <div className="py-12 text-center text-sm text-[#5A5D6E]">Sin items. Agregá productos.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#F7F7F9] border-b border-[#E2E4E9]">
              <tr>
                <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium w-32">Ref.</th>
                <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Descripción</th>
                <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium w-24">Cantidad</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-[#E2E4E9]">
                  <td className="p-1.5">
                    <Input
                      value={it.reference}
                      onChange={(e) => updateItem(i, "reference", e.target.value)}
                      className="h-8 rounded-sm font-mono text-xs"
                      data-testid={`item-ref-${i}`}
                    />
                  </td>
                  <td className="p-1.5"><Input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} className="h-8 rounded-sm" data-testid={`item-desc-${i}`} /></td>
                  <td className="p-1.5"><Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="h-8 rounded-sm" data-testid={`item-qty-${i}`} /></td>
                  <td className="p-1.5">
                    <button onClick={() => removeItem(i)} className="p-1.5 hover:bg-[#D32F2F]/10 text-[#D32F2F] rounded-sm" data-testid={`item-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-end gap-3 mt-6">
        <Button variant="outline" onClick={() => navigate("/plantillas")} className="rounded-sm">Cancelar</Button>
        <Button onClick={onSave} className="bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-sm" data-testid="template-save-button">
          Guardar plantilla
        </Button>
      </div>

      <AlertDialog open={!!importChoice} onOpenChange={(o) => !o && setImportChoice(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="import-choice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Importar items</AlertDialogTitle>
            <AlertDialogDescription>
              {importChoice && (
                <>
                  Encontramos <strong>{importChoice.count}</strong> item{importChoice.count !== 1 ? "s" : ""} en el archivo. La plantilla actual ya tiene <strong>{items.length}</strong> item{items.length !== 1 ? "s" : ""}.
                  <br /><br />
                  ¿Qué querés hacer?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 flex-col sm:flex-row">
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => applyImport("append")}
              className="rounded-sm"
              data-testid="import-append-button"
            >
              Añadir al final
            </Button>
            <AlertDialogAction
              onClick={() => applyImport("replace")}
              className="bg-[#D32F2F] hover:bg-[#D32F2F]/90 rounded-sm"
              data-testid="import-replace-button"
            >
              Reemplazar todo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
